import React, { useState, useEffect, useCallback } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import AlertsActive from './AlertsActive';
import AlertsHistory from './AlertsHistory';
import AlertRules from './AlertRules';
import AlertsSettings from './AlertsSettings';
import DevicesDashboard from './DevicesDashboard';
import './AlertsDashboard.css';

/**
 * AlertsDashboard - Main container for the Alerts functionality
 *
 * Props:
 *   - monitoringData: { devices, active_alerts, alert_history, configs }
 *   - onSaveConfig: function to save MonitoringControl_configs
 *   - onRefresh: function to refresh monitoring data
 *   - isLoading: boolean
 *   - error: string or null
 *   - saveStatus: 'success' | 'error' | ''
 */
const AlertsDashboard = ({
  monitoringData,
  onSaveConfig,
  onSaveDeviceNotes,
  onRefresh,
  isLoading,
  error,
  saveStatus
}) => {
  const [tabIndex, setTabIndex] = useState(0);

  // Extract data from monitoringData
  const devices = monitoringData?.devices || [];
  const activeAlerts = monitoringData?.active_alerts || [];
  const alertHistory = monitoringData?.alert_history || [];
  const configs = monitoringData?.configs || {};

  // Count active alerts for badge display
  const activeAlertCount = activeAlerts.length;

  // Handle config save
  const handleSaveConfig = useCallback((updatedConfigs) => {
    if (onSaveConfig) {
      onSaveConfig(updatedConfigs);
    }
  }, [onSaveConfig]);

  // If no monitoring data yet, show loading or prompt
  if (!monitoringData && !isLoading && !error) {
    return (
      <div className="alerts-dashboard">
        <div className="alerts-empty-state">
          <p>No monitoring data available.</p>
          <p>Monitoring may not be enabled for this account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alerts-dashboard">
      <div className="alerts-header">
        <h2>Devices & Monitoring</h2>
        <button
          className="refresh-button"
          onClick={onRefresh}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="alerts-error">
          {error}
        </div>
      )}

      <Tabs selectedIndex={tabIndex} onSelect={setTabIndex}>
        <TabList>
          <Tab>
            Devices
            {devices.length > 0 && (
              <span className="device-count-badge">{devices.length}</span>
            )}
          </Tab>
          <Tab>
            Alerts
            {activeAlertCount > 0 && (
              <span className="alert-badge">{activeAlertCount}</span>
            )}
          </Tab>
          <Tab>History</Tab>
          <Tab>Rules</Tab>
         {/*  <Tab>Settings</Tab>*/}
        </TabList>

        <TabPanel>
          <DevicesDashboard
            devices={devices}
            activeAlerts={activeAlerts}
            configs={configs}
            onSaveConfig={handleSaveConfig}
            onSaveDeviceNotes={onSaveDeviceNotes}
            isLoading={isLoading}
          />
        </TabPanel>

        <TabPanel>
          <AlertsActive
            alerts={activeAlerts}
            devices={devices}
            isLoading={isLoading}
          />
        </TabPanel>

        <TabPanel>
          <AlertsHistory
            history={alertHistory}
            devices={devices}
            isLoading={isLoading}
          />
        </TabPanel>

        <TabPanel>
          <AlertRules
            configs={configs}
            devices={devices}
            onSave={handleSaveConfig}
            saveStatus={saveStatus}
          />
        </TabPanel>

        <TabPanel>
          <AlertsSettings
            configs={configs}
            onSave={handleSaveConfig}
            saveStatus={saveStatus}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default AlertsDashboard;
